import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from './usePermissions';
import { apiService } from '../services/api';
import { FEATURE_FLAGS } from '../config/api.config';

export interface MoreOption {
  id: string;
  title: string;
  icon: string;
  action: () => void;
  showOnlyForRoles?: ('admin' | 'owner' | 'member')[];
  /** When set, the option additionally requires this permission-catalog key at
   * this level or above — evaluated as an OR with showOnlyForRoles (admin/owner
   * always see it regardless), not an AND, so a granted permission surfaces the
   * option for non-admin members too. */
  minPermission?: { key: string; level: 'view' | 'edit' | 'admin' };
}

/** Shared data/state/actions behind the account & business-switcher menu —
 * extracted so both MoreOptionsModal (native + mobile-web, full-screen
 * overlay) and AvatarMenu (desktop-web, anchored dropdown) consume the same
 * business-loading/image-fallback logic instead of two independent,
 * potentially-drifting copies. `onClose` is threaded through since each
 * option's action needs to close whichever presentational wrapper invoked it.
 * `active` gates the business-list fetch — both wrappers stay mounted while
 * closed (MoreOptionsModal always; AvatarMenu while animating out), so this
 * avoids fetching before the menu has ever been opened. Option titles and
 * notification strings are translated here (not by each consumer) so both
 * wrappers stay in sync automatically. */
export function useMoreOptionsData(onClose: () => void, active: boolean) {
  const { user, logout, currentBusiness: storeBusiness, businessRole, userBusinesses: storeBusinesses } = useAppStore();
  const { showNotification, navigateTo } = useAppContext();
  const { t } = useLocale();
  const { themeMode, setThemeMode } = useTheme();
  const { hasPermissionAtLeast } = usePermissions();

  const [localBusiness, setLocalBusiness] = useState<any>(null);
  const [localBusinesses, setLocalBusinesses] = useState<any[]>([]);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const [imageError, setImageError] = useState(false);

  const currentBusiness = storeBusiness || localBusiness;
  const userBusinesses = storeBusinesses.length > 0 ? storeBusinesses : localBusinesses;

  const loadUserBusinesses = async () => {
    try {
      setIsLoadingBusinesses(true);
      const response = await apiService.getBusinesses();
      if (response.statusCode === 1000 && response.data) {
        setLocalBusinesses(response.data);
        if (response.data.length > 0 && !storeBusiness) {
          setLocalBusiness(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load user businesses:', error);
    } finally {
      setIsLoadingBusinesses(false);
    }
  };

  useEffect(() => {
    if (active && user) {
      loadUserBusinesses();
    }
  }, [active, user]);

  const profilePictureUrl = user?.avatar || user?.profilePicture;
  useEffect(() => {
    setImageError(false);
  }, [profilePictureUrl]);

  const switchBusiness = () => {
    onClose();
    navigateTo('business-picker');
  };

  const createBusiness = () => {
    onClose();
    navigateTo('business-creation-wizard');
  };

  const allOptions: MoreOption[] = [
    {
      // "Get your business ready" checklist (owners/admins; same screen as onboarding's last step)
      id: 'setup-progress',
      title: t('avatarMenu.option.setupProgress'),
      icon: 'rocket-outline',
      action: () => { navigateTo('channel-onboarding-stepper'); onClose(); },
      showOnlyForRoles: ['admin', 'owner'],
    },
    {
      id: 'user-setup',
      title: t('avatarMenu.option.userManagement'),
      icon: 'people-outline',
      action: () => { navigateTo('user-setup'); onClose(); },
      showOnlyForRoles: ['admin', 'owner'],
    },
    {
      id: 'departments',
      title: t('avatarMenu.option.departments'),
      icon: 'business-outline',
      action: () => { navigateTo('departments'); onClose(); },
      showOnlyForRoles: ['admin', 'owner'],
    },
    {
      id: 'subscription',
      title: t('avatarMenu.option.subscription'),
      icon: 'card-outline',
      action: () => { navigateTo('subscription'); onClose(); },
      showOnlyForRoles: ['admin', 'owner'],
    },
    {
      id: 'products',
      title: t('avatarMenu.option.products'),
      icon: 'cube-outline',
      action: () => { navigateTo('products-list'); onClose(); },
    },
    {
      id: 'lead-finder',
      title: t('leadFinder.title'),
      icon: 'search-circle-outline',
      action: () => { navigateTo('lead-finder'); onClose(); },
      showOnlyForRoles: ['admin', 'owner'],
      minPermission: { key: 'general.leadFinder', level: 'view' },
    },
    {
      id: 'task-history',
      title: t('avatarMenu.option.taskHistory'),
      icon: 'document-text-outline',
      action: () => { navigateTo('task-history'); onClose(); },
      showOnlyForRoles: ['admin', 'owner'],
      minPermission: { key: 'task.viewHistory', level: 'view' },
    },
    {
      id: 'notifications',
      title: t('avatarMenu.option.notifications'),
      icon: 'notifications-outline',
      action: () => { navigateTo('notifications'); onClose(); },
    },
    {
      // Moved out of the header: one tap, the menu stays open so the change is seen at once.
      id: 'theme',
      title: themeMode === 'dark' ? t('avatarMenu.option.lightMode') : t('avatarMenu.option.darkMode'),
      icon: themeMode === 'dark' ? 'sunny-outline' : 'moon-outline',
      action: () => setThemeMode(themeMode === 'dark' ? 'light' : 'dark'),
    },
    {
      id: 'settings',
      title: t('avatarMenu.option.settings'),
      icon: 'settings-outline',
      action: () => { navigateTo('settings'); onClose(); },
    },
    {
      id: 'faq',
      title: t('avatarMenu.option.faq'),
      icon: 'help-circle-outline',
      action: () => {
        showNotification('info', t('avatarMenu.faq.title'), t('avatarMenu.faq.message'));
        onClose();
      },
    },
    {
      id: 'logout',
      title: t('avatarMenu.option.logout'),
      icon: 'log-out-outline',
      action: () => {
        logout();
        showNotification('success', t('avatarMenu.logout.title'), t('avatarMenu.logout.message'));
        navigateTo('welcome');
        onClose();
      },
    },
  ];

  const moreOptions = allOptions.filter((option) => {
    const roleAllows = !option.showOnlyForRoles || (!!businessRole && option.showOnlyForRoles.includes(businessRole));
    if (option.minPermission) {
      return roleAllows || hasPermissionAtLeast(option.minPermission.key, option.minPermission.level);
    }
    return roleAllows;
  });

  return {
    user,
    currentBusiness,
    userBusinesses,
    businessRole,
    isLoadingBusinesses,
    imageError,
    setImageError,
    moreOptions,
    switchBusiness,
    createBusiness,
  };
}
